import {getConnectedEdges, getIncomers, getOutgoers} from 'reactflow';
import DFS from './DFS';
import Issue from './Issue';
import IssueType from './IssueType';
import IssueIntensity from './IssueIntensity';

// Output handles per node type, see the OutputHandle ids in components/nodes.
// Derived from nodeType so validation also works on stored flowData, which has
// no React Flow internals (handleBounds) attached.
const outputHandleCount = (node) => node.data?.nodeType === 'decideNode' ? 2 : 1

// Plain, serializable result for use outside the editor (store, selectors):
// [{ type, intensity, nodeIds }]
export function validateFlowData(flowData) {
    if (!flowData?.nodes?.length || !flowData.edges) {
        return []
    }
    return validation(flowData.nodes, flowData.edges).map(issue => ({
        type: issue.type,
        intensity: issue.intensity,
        nodeIds: issue.affectedNodes.map(node => node.id)
    }))
}

export default function validation(nodes, edges) {
    let issues = []

    // start and end are found by id, their position in nodes is not fixed
    // (e.g. AI generated flows append the end node after the steps)
    const startNode = nodes.find(node => node.id === 'startOfWorkflow')
    const endNode = nodes.find(node => node.id === 'endOfWorkflow')
    if (!startNode || !endNode) {
        return [new Issue(IssueType.NoValidWay, IssueIntensity.Error, [startNode, endNode].filter(Boolean))]
    }

    const dfs = new DFS(nodes, edges)

    dfs.dfsValidWay(startNode)
    dfs.dfsFromEnd(endNode)
    dfs.dfsBidirectional(startNode)
    dfs.dfsBidirectional(endNode)
    dfs.trajansSCCAlgorithm()

    if (!startNode.data.title) {
        issues.push(new Issue(IssueType.noTitle, IssueIntensity.Info, [startNode]))
    }

    if (!dfs.validWay) {
        let affectedNodes = []

        if (getOutgoers(startNode, nodes, edges).length === 0) {
            affectedNodes.push(startNode)
        }
        if (getIncomers(endNode, nodes, edges).length === 0) {
            affectedNodes.push(endNode)
        }

        issues.push(new Issue(IssueType.NoValidWay, IssueIntensity.Error, affectedNodes))
    }

    for (const node of nodes) {

        if (node.id === 'startOfWorkflow' || node.id === 'endOfWorkflow') {
            continue
        }

        const numberOfOutputHandles = outputHandleCount(node)
        const outgoers = getOutgoers(node, nodes, edges)

        if (numberOfOutputHandles !== outgoers.length) {
            const connectedEdges = getConnectedEdges([node], edges)
            let outputs = 0
            for (let j = 0; j < connectedEdges.length; j++) {
                if (connectedEdges[j].source === node.id) {
                    outputs++
                }
            }
            if (numberOfOutputHandles === outputs) {
                issues.push(new Issue(IssueType.Redundancy, IssueIntensity.Info, [node]))
            } else if (dfs.reachableFromStart.includes(node.id)) {
                issues.push(new Issue(IssueType.DeadEnd, IssueIntensity.Error, [node]))
            }
        }

        const incomers = getIncomers(node, nodes, edges)

        if (!dfs.mainStrain.includes(node.id) && !dfs.looseConstruct.includes(node.id)) {
            let affectedNodes = []
            dfs.dfsLooseConstruct(node, affectedNodes)
            issues.push(new Issue(IssueType.LooseConstruct, IssueIntensity.Info, affectedNodes))
        } else if (incomers.length < 1 && dfs.mainStrain.includes(node.id)) {
            if (!dfs.validWay) {
                issues.push(new Issue(IssueType.NoIncomer, IssueIntensity.Error, [node]))
            } else {
                issues.push(new Issue(IssueType.NoIncomer, IssueIntensity.Info, [node]))
            }
        }
    }

    for (const cycle of dfs.cycles) {

        if (dfs.mainStrain.includes(cycle[0].id) && !dfs.reachableFromStart.includes(cycle[0].id) && !dfs.unreachable.includes(cycle[0].id)) {
            let affectedNodes = []
            dfs.dfsUntilReachable(cycle[0], affectedNodes)
            issues.push(new Issue(IssueType.NodeUnreachable, IssueIntensity.Info, affectedNodes))
        }
        if (dfs.reachableFromEnd.includes(cycle[0].id)) {
            continue
        }
        if (dfs.reachableFromStart.includes(cycle[0].id)) {
            issues.push(new Issue(IssueType.BadCycle, IssueIntensity.Error, cycle))
        }
    }

    return issues
}